"use client"

import { useEffect, useRef, useState } from "react"
import { Minus, Plus, X } from "lucide-react"
import { useSelector } from "react-redux"

import { useCart } from "../../context/CartContext"

const Sellers = ({ products, loading, error }) => {
  const owlCarouselRef = useRef(null)
  const [animatingCounters, setAnimatingCounters] = useState({})
  const [animationDirection, setAnimationDirection] = useState({})
  const [currentSlides, setCurrentSlides] = useState({})
  const [showLoginModal, setShowLoginModal] = useState(false)
  const slideIntervalRef = useRef(null)
  const slidersRef = useRef({})
  const touchStartXRef = useRef({})
  // Get user from Redux state
  const user = useSelector((state) => state.auth.user)
  const { cart, cartItems, addToCart, updateQuantity } = useCart()

  // Function to handle redirection to login page
  const redirectToLogin = () => {
    window.location.href = "/login"
  }

  // Function to handle product click
  const handleProductClick = (productId) => {
    // Redirect to single product page with product ID
    window.location.href = `/single-product?id=${productId}`
  }

  const handleAddToCart = (productId, index) => {
    // Check if user is logged in
    if (!user) {
      setShowLoginModal(true)
      return
    }

    const itemKey = `${productId}-${index}`

    // Set animation direction to bottom-to-top
    setAnimationDirection((prev) => ({
      ...prev,
      [itemKey]: "up",
    }))

    // Trigger animation
    setAnimatingCounters((prev) => ({
      ...prev,
      [itemKey]: true,
    }))

    // Reset animation after duration
    setTimeout(() => {
      setAnimatingCounters((prev) => ({
        ...prev,
        [itemKey]: false,
      }))
    }, 500)

    // Call the context method to add to cart
    addToCart(productId)
  }

  const increaseQuantity = (productId, index) => {
    const itemKey = `${productId}-${index}`

    // Set animation direction to bottom-to-top
    setAnimationDirection((prev) => ({
      ...prev,
      [itemKey]: "up",
    }))

    // Trigger animation
    setAnimatingCounters((prev) => ({
      ...prev,
      [itemKey]: true,
    }))
    // Reset animation after duration
    setTimeout(() => {
      setAnimatingCounters((prev) => ({
        ...prev,
        [itemKey]: false,
      }))
    }, 500)

    // Call the context method to update quantity
    updateQuantity(productId, (cart[productId] || 0) + 1)
  }

  const decreaseQuantity = (productId, index) => {
    const itemKey = `${productId}-${index}`

    // Set animation direction to top-to-bottom
    setAnimationDirection((prev) => ({
      ...prev,
      [itemKey]: "down",
    }))

    // Trigger animation
    setAnimatingCounters((prev) => ({
      ...prev,
      [itemKey]: true,
    }))

    // Reset animation after duration
    setTimeout(() => {
      setAnimatingCounters((prev) => ({
        ...prev,
        [itemKey]: false,
      }))
    }, 500)

    // Call the context method to update quantity
    updateQuantity(productId, (cart[productId] || 0) - 1)
  }

  // Sync local cart items with context cart items
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const newLocalCart = {}
      cartItems.forEach((item) => {
        // Create a key for each item in the format expected by the component
        products.forEach((product, index) => {
          if (product._id === item.productId) {
            const itemKey = `${item.productId}-${index}`
            newLocalCart[itemKey] = item.quantity
          }
        })
      })
    }
  }, [cartItems, products])

  const formatPrice = (price) => {
    const parts = price.toFixed(2).split(".")
    return {
      dollars: parts[0],
      cents: parts[1],
    }
  }

  // Function to go to next slide for a product
  const nextSlide = (productId) => {
    const product = products.find((p) => p._id === productId)
    if (!product) return

    const combinedImages = [product.mainImage, ...(product.otherImages || [])]
    const imageCount = combinedImages.length
    if (imageCount <= 1) return

    setCurrentSlides((prev) => {
      const currentIndex = prev[productId] || 0
      const nextIndex = (currentIndex + 1) % imageCount
      return { ...prev, [productId]: nextIndex }
    })
  }

  // Function to go to previous slide for a product
  const prevSlide = (productId) => {
    const product = products.find((p) => p._id === productId)
    if (!product) return

    const combinedImages = [product.mainImage, ...(product.otherImages || [])]
    const imageCount = combinedImages.length
    if (imageCount <= 1) return

    setCurrentSlides((prev) => {
      const currentIndex = prev[productId] || 0
      const prevIndex = (currentIndex - 1 + imageCount) % imageCount
      return { ...prev, [productId]: prevIndex }
    })
  }

  // Handle mouse enter for product
  const handleMouseEnter = (productId) => {

    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current)
    }

    const product = products.find((p) => p._id === productId)
    if (product && product.otherImages && product.otherImages.length > 0) {
      slideIntervalRef.current = setInterval(() => {
        nextSlide(productId)
      }, 1500) // Change slide every 1.5 seconds on hover
    }
  }

  // Handle mouse leave for product
  const handleMouseLeave = () => {

    if (slideIntervalRef.current) {
      clearInterval(slideIntervalRef.current)
      slideIntervalRef.current = null
    }
  }

  // Touch event handlers for slider
  const handleTouchStart = (productId, e) => {
    touchStartXRef.current[productId] = e.touches[0].clientX
  }

  const handleTouchEnd = (productId, e) => {
    if (!touchStartXRef.current[productId]) return

    const touchEndX = e.changedTouches[0].clientX
    const diff = touchStartXRef.current[productId] - touchEndX

    if (Math.abs(diff) > 50) {
      // Minimum swipe distance
      if (diff > 0) {
        nextSlide(productId)
      } else {
        prevSlide(productId)
      }
    }

    touchStartXRef.current[productId] = null
  }

  useEffect(() => {
    // Custom Owl Carousel CSS to implement 3-dot cycling for all devices
    const injectCustomCarouselCSS = () => {
      const styleElement = document.createElement("style")
      styleElement.textContent = `
          /* Apply to all device sizes - not just mobile */
          .owl-carousel .owl-dots {
            display: flex;
            justify-content: center;
          }
          
          /* Hide all dots by default */
          .owl-carousel .owl-dots .owl-dot {
            display: none;
          }
          
          /* Show only first three dots */
          .owl-carousel .owl-dots .owl-dot:nth-child(1),
          .owl-carousel .owl-dots .owl-dot:nth-child(2),
          .owl-carousel .owl-dots .owl-dot:nth-child(3) {
            display: inline-block;
          }
          
          /* Make dots bigger and more visible */
          .owl-carousel .owl-dots .owl-dot span {
            width: 12px;
            height: 12px;
            margin: 5px 7px;
          }
          
          /* Animation for dot transition */
          @keyframes dotTransition {
            0% { transform: scale(0.8); opacity: 0.5; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          .owl-dot.animating {
            animation: dotTransition 0.3s ease-in-out;
          }
          
          /* Add cursor pointer to product images */
          .classic_image_box figure img {
            cursor: pointer;
          }
          
          /* Login modal styles */
          .login-modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }
          
          .login-modal {
            background-color: white;
            border-radius: 8px;
            padding: 20px;
            width: 90%;
            max-width: 400px;
            position: relative;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          }
          
          .login-modal-close {
            position: absolute;
            top: 10px;
            right: 10px;
            cursor: pointer;
            color: #666;
          }
          
          .login-modal-title {
            margin-top: 10px;
            margin-bottom: 20px;
            font-size: 24px;
            font-weight: 600;
            color: #333;
          }
          
          .login-modal-content p {
            margin-bottom: 20px;
            font-size: 16px;
            color: #555;
          }
          
          .login-modal-buttons {
            display: flex;
            justify-content: space-between;
            margin-top: 20px;
          }
          
          .login-button {
            padding: 10px 55px;
            background: linear-gradient(90deg, #E0A323 0%, #FEBA2A 100%);
            color: white;
            border: none;
            border-radius: 4px;
            font-weight: 600;
            cursor: pointer;
            min-width: 100px;
          }
          
          .cancel-button {
            padding: 10px 20px;
            background-color: #f1f1f1;
            color: #333;
            border: none;
            border-radius: 4px;
            font-weight: 600;
            cursor: pointer;
            min-width: 100px;
          }

          .creation-items {
            width: 78%;
            margin: auto;
          }
          
          @keyframes countUp {
            0% {
              transform: translateY(10px);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes countDown {
            0% {
              transform: translateY(-10px);
              opacity: 0;
            }
            100% {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          .counter-animation-up {
            animation: countUp 0.5s ease-out forwards;
            display: inline-block;
          }
          
          .counter-animation-down {
            animation: countDown 0.5s ease-out forwards;
            display: inline-block;
          }
          
          .quantity-selector {
            animation: fadeIn 0.3s ease-out forwards;
          }
          
          @keyframes fadeIn {
            0% {
              opacity: 0;
              transform: scale(0.9);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          .classic_image_box {
            position: relative;
            overflow: hidden;
          }
          
          .classic_image_box figure {
            position: relative;
            cursor: pointer;
          }
          
          .classic_image_box img {
            transition: transform 0.3s ease;
          }
          
          .classic_image_box:hover img {
            transform: scale(1.05);
          }
          
          .image-slider-dots {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          
          .classic_image_box:hover .image-slider-dots {
            opacity: 1;
          }

          /* Image slider styles */
          .slider-container {
            transition: transform 0.3s ease;
            display: flex;
            height: 100%;
            width: 100%;
          }
          
          .slide {
            min-width: 100%;
            height: 100%;
            flex-shrink: 0;
          }
          
          .slide-img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            object-position: center;
          }
          
          .slider-nav-btn {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            background: rgba(255, 255, 255, 0.7);
            border-radius: 50%;
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            cursor: pointer;
            z-index: 2;
          }
          
          .prev-btn {
            left: 5px;
          }
          
          .next-btn {
            right: 5px;
          }
          
          .slider-indicators {
            position: absolute;
            bottom: 10px;
            left: 0;
            right: 0;
            display: flex;
            justify-content: center;
            gap: 5px;
            z-index: 2;
          }
          
          .indicator {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.7);
            cursor: pointer;
          }
          
          .indicator.active {
            background: #feba2a;
          }
        `
      document.head.appendChild(styleElement)
      return styleElement
    }

    const initializeCustomDotNavigation = () => {
      if (!window.jQuery || typeof window.jQuery.fn.owlCarousel !== "function") {
        setTimeout(initializeCustomDotNavigation, 500)
        return
      }

      const $ = window.jQuery

      // Initialize the carousel
      // eslint-disable-next-line no-unused-vars
      $(owlCarouselRef.current).owlCarousel({
        loop: true,
        margin: 20,
        nav: false,
        dots: true,
        dotsEach: 1,
        responsive: {
          0: { items: 1 },
          576: { items: 2 },
          992: { items: 3 },
          1200: { items: 4 },
        },
        onInitialized: (event) => {
          // Create only 3 custom dots replacing native dots
          customizeDots()
        },
        onChanged: (event) => {
          // Get the current position
          const current = event.item.index
          // Calculate which of the 3 dots should be active (using modulo)
          const activeDotIndex = (current % 3) + 1

          // Update the active dot
          $(".owl-dot").removeClass("active animating")
          $(`.owl-dot:nth-child(${activeDotIndex})`).addClass("active animating")
        },
      })

      // Function to limit and customize dots
      function customizeDots() {
        // First make sure there are only 3 dots
        $(".owl-dot:nth-child(n+4)").remove()

        // Make the first dot active initially
        $(".owl-dot:first-child").addClass("active")

        // Add click handlers to dots that adjust based on current position
        $(".owl-dot").each(function (index) {
          $(this)
            .off("click")
            .on("click", () => {
              // Calculate the target slide based on current position and clicked dot
              const currentPos = $(owlCarouselRef.current).find(".owl-item.active").first().index()
              const baseSlide = Math.floor(currentPos / 3) * 3
              const targetSlide = baseSlide + index

              // Go to that slide
              $(owlCarouselRef.current).trigger("to.owl.carousel", [targetSlide, 1000])
            })
        })
      }
    }

    const styleElement = injectCustomCarouselCSS()
    const carouselElement = owlCarouselRef.current // Copy ref to a local variable

    // Only initialize carousel when products are loaded
    if (products.length > 0) {
      initializeCustomDotNavigation()
    }

    return () => {
      if (window.jQuery && carouselElement) {
        try {
          window.jQuery(carouselElement).owlCarousel("destroy")
        } catch (e) {
          console.error('Could not destroy carousel', e)
        }
      }

      // Remove custom styles on component unmount
      if (styleElement) {
        document.head.removeChild(styleElement)
      }

      // Clear any active slide intervals
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current)
      }
    }
  }, [products])

  // Render loading state
  if (loading) {
    return (
      <section className="classic-con position-relative">
        <div className="creation-items position-relative">
          <div className="row">
            <div className="col-12">
              <div className="classic_content text-center" data-aos="fade-up">
                <h2>
                  Our <span>Best</span> Sellers
                </h2>
                <p>Loading our delicious products...</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  // Render error state
  if (error) {
    return (
      <section className="classic-con position-relative">
        <div className="creation-items position-relative">
          <div className="row">
            <div className="col-12">
              <div className="classic_content text-center" data-aos="fade-up">
                <h2>
                  Our <span>Best</span> Sellers
                </h2>
                <p>Error loading products. Please try again later.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  // Calculate animation class based on direction and animation state
  const getAnimationClass = (itemKey) => {
    if (!animatingCounters[itemKey]) return ""

    return animationDirection[itemKey] === "up" ? "counter-animation-up" : "counter-animation-down"
  }

  const getBoxClass = (index) => {
    const boxClasses = ["box1", "box2", "box3", "box4"]
    return boxClasses[index % boxClasses.length]
  }

  // Create a repeated array for carousel if needed
  const displayProducts = products.length > 0 ? [...products] : []

  return (
    <>
      {showLoginModal && (
        <div className="login-modal-overlay">
          <div className="login-modal">
            <div className="login-modal-close" onClick={() => setShowLoginModal(false)}>
              <X size={24} />
            </div>
            <h3 className="login-modal-title">Login Required</h3>
            <div className="login-modal-content">
              <p>Please login to add items to your cart.</p>
            </div>
            <div className="login-modal-buttons">
              <button className="login-button" onClick={redirectToLogin}>
                Login
              </button>
              <button className="cancel-button" onClick={() => setShowLoginModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="classic-con position-relative">
        <figure className="classic-leftimage mb-0">
          <img
            src="https://res.cloudinary.com/dzk6ng1ba/image/upload/v1743069237/T01_hwn5yp.png"
            alt="Classic left decoration"
            className="img-fluid"
            style={{ display: "none", width: "100%", height: "100%" }}
          />
        </figure>
        <figure className="classic-rightimage mb-0" style={{ transform: "rotate(60deg)" }}>
          <img
            src="https://res.cloudinary.com/dzk6ng1ba/image/upload/v1743069238/T03_myejap.png"
            alt="Classic right decoration"
            className="img-fluid"
            style={{ display: "none", width: "100%", height: "100%" }}
          />
        </figure>
        <div className="creation-items position-relative">
          <div className="row">
            <div className="col-12">
              <div className="classic_content text-center" data-aos="fade-up">
                <h2>
                  Our <span>Best</span> Sellers
                </h2>
                <p>Discover the favorites that keep our customers coming back for more.</p>
              </div>
            </div>
          </div>
          <div className="classic_wrapper" data-aos="fade-up">
            <div className="row">
              <div className="col-12">
                <div ref={owlCarouselRef} className="owl-carousel owl-theme">
                  {displayProducts.map((product, index) => {
                    const priceParts = formatPrice(product.mrp || 0)
                    const sellerParts = formatPrice(product.sellingPrice)
                    const itemKey = `${product._id}-${index}`
                    const animationClass = getAnimationClass(itemKey)
                    const boxClass = getBoxClass(index)
                    const currentSlideIndex = currentSlides[product._id] || 0
                    const combinedBothImages = [product.mainImage, ...(product.otherImages || [])]

                    return (
                      <div className="item" key={index}>
                        <div className="classic-box">
                          <div
                            className={`classic_image_box ${boxClass}`}
                            style={{
                              height: '300px',
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            <div
                              className="image-slider"
                              ref={(el) => (slidersRef.current[product._id] = el)}
                              onMouseEnter={() => handleMouseEnter(product._id)}
                              onMouseLeave={handleMouseLeave}
                              onTouchStart={(e) => handleTouchStart(product._id, e)}
                              onTouchEnd={(e) => handleTouchEnd(product._id, e)}
                              onClick={() => handleProductClick(product._id)}
                              style={{
                                cursor: "pointer",
                                position: "relative",
                                height: "100%",
                                width: "100%"
                              }}
                            >
                              <div
                                className="slider-container"
                                style={{
                                  transform: `translateX(-${currentSlideIndex * 100}%)`,
                                  height: "100%",
                                  width: "100%"
                                }}
                              >
                                {combinedBothImages.map((image, idx) => (
                                  <div
                                    className="slide"
                                    key={idx}
                                    style={{
                                      minWidth: "100%",
                                      height: "100%",
                                      flexShrink: 0
                                    }}
                                  >
                                    <img
                                      src={image || "/placeholder.svg"}
                                      alt={`${product.name} - ${idx + 1}`}
                                      className="slide-img"
                                      loading="lazy"
                                      style={{
                                        width: "100%",
                                        height: "75%",
                                        objectFit: "contain",
                                        objectPosition: "center"
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="classic_box_content">
                            <div className="text_wrapper position-relative">
                              <h6
                                className="pttext"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleProductClick(product._id)}
                              >
                                {product.name.split(' ').slice(0, 2).join(' ')}
                              </h6>
                              <div className="rating">
                                <i className="fa-solid fa-star"></i>
                                <span>4.9/5</span>
                              </div>
                            </div>
                            <p className="text-size-16">{product.description.slice(0, 30)}</p>
                            <div className="price_wrapper position-relative">
                              <span className="dollar">
                                ₹<span className="counter">{sellerParts.dollars}</span> <span
                                  className="line-through-price">₹{priceParts.dollars}</span>
                              </span>

                              {user && cart && cart[product._id] ? (
                                <div
                                  className="quantity-selector"
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    backgroundColor: "#fff",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "50px",
                                    padding: "3px",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                                    width: "fit-content",
                                  }}
                                >
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      decreaseQuantity(product._id, index);
                                    }}
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      border: "none",
                                      backgroundColor: "transparent",
                                      color: "#333",
                                      borderRadius: "50%",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <Minus size={16} />
                                  </button>
                                  <span
                                    className={animationClass}
                                    style={{
                                      margin: "0 15px",
                                      fontWeight: "400",
                                      color: "black",
                                      fontSize: "16px",
                                    }}
                                  >
                                    {cart[product._id]}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      increaseQuantity(product._id, index);
                                    }}
                                    style={{
                                      width: "28px",
                                      height: "28px",
                                      border: "none",
                                      backgroundColor: "transparent",
                                      color: "#333",
                                      borderRadius: "50%",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                      transition: "all 0.2s ease",
                                    }}
                                  >
                                    <Plus size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddToCart(product._id, index);
                                  }}
                                  style={{
                                    background: "linear-gradient(90deg, #E0A323 0%, #FEBA2A 100%)",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "50px",
                                    padding: "8px 15px",
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                                  }}
                                >
                                  Add to Cart
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Sellers